import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Check, ChevronRight, Store, FileSignature, Users, Zap,
  ArrowLeft, Clock, CheckCircle2, Circle, Loader2
} from 'lucide-react';
import api from '../../api/client';
import { toast } from 'sonner';
import { useCurrency } from '../../context/CurrencyContext';

const STEPS = [
  { key: '1_store_info', label: 'Store Information', icon: Store, description: 'Create the store profile' },
  { key: '2_agreement', label: 'Agreement Signing', icon: FileSignature, description: 'Set up merchant agreement' },
  { key: '3_user_setup', label: 'User Setup', icon: Users, description: 'Create merchant login' },
  { key: '4_test_transaction', label: 'Test Transaction', icon: Zap, description: 'Verify the setup works' },
];

const STATUS_COLORS = {
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  abandoned: 'bg-gray-100 text-gray-500',
};

function StepIndicator({ steps, currentStep }) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="step-indicator">
      {STEPS.map((step, i) => {
        const stepData = steps?.[step.key];
        const isCompleted = stepData?.status === 'completed';
        const isCurrent = i + 1 === currentStep && !isCompleted;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            {i > 0 && <div className={`w-8 h-0.5 flex-shrink-0 ${isCompleted || i < currentStep - 1 ? 'bg-emerald-400' : 'bg-gray-200'}`} />}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl flex-shrink-0 text-sm font-medium transition-all ${
              isCompleted ? 'bg-emerald-50 text-emerald-700' :
              isCurrent ? 'bg-[#561D1A] text-white' :
              'bg-gray-100 text-gray-400'
            }`} data-testid={`step-${i + 1}`}>
              {isCompleted ? <CheckCircle2 size={16} /> : isCurrent ? <Loader2 size={16} className="animate-spin" /> : <Circle size={16} />}
              <span className="hidden sm:inline">{step.label}</span>
              <span className="sm:hidden">{i + 1}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Step1Form({ sessionId, onComplete }) {
  const [form, setForm] = useState({ name: '', category: '', location_floor: '', location_unit: '', contact_person: '', contact_email: '', contact_phone: '', settlement_frequency: 'monthly', commission_percent: 10 });
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!form.name) { toast.error('Store name is required'); return; }
    setSaving(true);
    try {
      await api.put(`/api/store/admin/onboarding/sessions/${sessionId}/step1`, form);
      toast.success('Store created');
      onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step1-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-500 uppercase">Store Name *</label>
          <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="store-name-input" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Category</label>
          <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} placeholder="e.g. Fashion, Electronics"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="store-category-input" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Floor</label>
          <input value={form.location_floor} onChange={e => setForm(f => ({...f, location_floor: e.target.value}))} placeholder="e.g. 2nd Floor"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Unit</label>
          <input value={form.location_unit} onChange={e => setForm(f => ({...f, location_unit: e.target.value}))} placeholder="e.g. Unit A-12"
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Contact Person</label>
          <input value={form.contact_person} onChange={e => setForm(f => ({...f, contact_person: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Contact Email</label>
          <input type="email" value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Contact Phone</label>
          <input value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Settlement Frequency</label>
          <select value={form.settlement_frequency} onChange={e => setForm(f => ({...f, settlement_frequency: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A] bg-white">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Commission %</label>
          <input type="number" min="0" max="100" value={form.commission_percent} onChange={e => setForm(f => ({...f, commission_percent: parseFloat(e.target.value) || 0}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handle} disabled={saving}
          className="px-6 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50 flex items-center gap-2" data-testid="step1-submit">
          {saving ? 'Creating...' : 'Create Store & Continue'} <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Step2Form({ sessionId, onComplete }) {
  const [form, setForm] = useState({ validity_months: 12, commission_percent: 10, merchant_contact_email: '' });
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await api.put(`/api/store/admin/onboarding/sessions/${sessionId}/step2`, form);
      toast.success('Agreement signed');
      onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step2-form">
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 mb-4">
        <p className="text-sm text-amber-800">This step auto-generates a merchant agreement. Both parties are marked as signed for onboarding purposes.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-500 uppercase">Validity (months)</label>
          <input type="number" min="1" value={form.validity_months} onChange={e => setForm(f => ({...f, validity_months: parseInt(e.target.value) || 12}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Commission %</label>
          <input type="number" min="0" max="100" value={form.commission_percent} onChange={e => setForm(f => ({...f, commission_percent: parseFloat(e.target.value) || 0}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-gray-500 uppercase">Merchant Contact Email</label>
          <input type="email" value={form.merchant_contact_email} onChange={e => setForm(f => ({...f, merchant_contact_email: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handle} disabled={saving}
          className="px-6 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50 flex items-center gap-2" data-testid="step2-submit">
          {saving ? 'Signing...' : 'Sign Agreement & Continue'} <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Step3Form({ sessionId, onComplete }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: 'TempPass123!' });
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      const res = await api.put(`/api/store/admin/onboarding/sessions/${sessionId}/step3`, form);
      toast.success(`Merchant user created. Temp password: ${res.data.temp_password}`);
      onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step3-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-500 uppercase">Full Name *</label>
          <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="user-name-input" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Email *</label>
          <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="user-email-input" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Phone</label>
          <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Temporary Password</label>
          <input value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A] font-mono" />
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={handle} disabled={saving}
          className="px-6 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50 flex items-center gap-2" data-testid="step3-submit">
          {saving ? 'Creating...' : 'Create User & Continue'} <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

function Step4Form({ sessionId, onComplete }) {
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await api.put(`/api/store/admin/onboarding/sessions/${sessionId}/step4`, {});
      toast.success('Onboarding complete! Store is now active.');
      onComplete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 text-center" data-testid="step4-form">
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
        <Zap size={32} className="mx-auto mb-3 text-blue-600" />
        <h4 className="text-base font-bold text-gray-800 mb-2">Run Test Transaction</h4>
        <p className="text-sm text-gray-600 max-w-md mx-auto">This will simulate a test redemption to verify the store is properly configured. The test data will be cleaned up automatically.</p>
      </div>
      <button onClick={handle} disabled={saving}
        className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 mx-auto" data-testid="step4-submit">
        {saving ? 'Running Test...' : 'Run Test & Complete Onboarding'} <Check size={16} />
      </button>
    </div>
  );
}

export default function OnboardingTab() {
  const { formatShortDate } = useCurrency();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/store/admin/onboarding/sessions');
      setSessions(res.data.sessions);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const createSession = async () => {
    if (!newName.trim()) { toast.error('Store name is required'); return; }
    setCreating(true);
    try {
      const res = await api.post('/api/store/admin/onboarding/sessions', { store_name: newName });
      setActiveSession(res.data);
      setShowCreate(false);
      setNewName('');
      toast.success('Onboarding session started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  const refreshSession = async () => {
    if (!activeSession) return;
    try {
      const res = await api.get(`/api/store/admin/onboarding/sessions/${activeSession.id}`);
      setActiveSession(res.data);
    } catch { /* ignore */ }
  };

  const handleStepComplete = async () => {
    await refreshSession();
    // Check if session was just refreshed and it says completed
    try {
      const res = await api.get(`/api/store/admin/onboarding/sessions/${activeSession.id}`);
      if (res.data.status === 'completed') {
        setActiveSession(null);
        loadSessions();
      } else {
        setActiveSession(res.data);
      }
    } catch { /* ignore */ }
  };

  const openSession = async (session) => {
    try {
      const res = await api.get(`/api/store/admin/onboarding/sessions/${session.id}`);
      setActiveSession(res.data);
    } catch { setActiveSession(session); }
  };

  // Active wizard view
  if (activeSession) {
    const currentStep = activeSession.current_step || 1;
    const isCompleted = activeSession.status === 'completed';

    return (
      <div className="space-y-6" data-testid="onboarding-wizard">
        <button onClick={() => { setActiveSession(null); loadSessions(); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#561D1A] transition-colors" data-testid="back-to-sessions">
          <ArrowLeft size={16} /> Back to Sessions
        </button>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-1">Onboarding: {activeSession.store_name}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {isCompleted ? 'Onboarding completed successfully!' : `Step ${currentStep} of 4`}
          </p>
          <StepIndicator steps={activeSession.steps} currentStep={currentStep} />
        </div>

        {isCompleted ? (
          <div className="bg-emerald-50 rounded-2xl p-8 border border-emerald-100 text-center">
            <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-500" />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Onboarding Complete!</h3>
            <p className="text-sm text-gray-600">The store is now active and ready to accept voucher redemptions.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <div className="mb-4">
              <h3 className="text-base font-bold text-gray-800">{STEPS[currentStep - 1]?.label}</h3>
              <p className="text-sm text-gray-500">{STEPS[currentStep - 1]?.description}</p>
            </div>
            {currentStep === 1 && <Step1Form sessionId={activeSession.id} onComplete={handleStepComplete} />}
            {currentStep === 2 && <Step2Form sessionId={activeSession.id} onComplete={handleStepComplete} />}
            {currentStep === 3 && <Step3Form sessionId={activeSession.id} onComplete={handleStepComplete} />}
            {currentStep === 4 && <Step4Form sessionId={activeSession.id} onComplete={handleStepComplete} />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Store Onboarding</h2>
          <p className="text-sm text-gray-500">Guided setup for new merchants</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="px-5 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] flex items-center gap-2 transition-all hover:shadow-lg" data-testid="start-onboarding-btn">
          <Plus size={16} /> Start Onboarding
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="create-session-modal">
            <h3 className="text-lg font-bold text-gray-800 mb-4">New Store Onboarding</h3>
            <div>
              <label className="text-xs text-gray-500 uppercase">Store Name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Enter store name"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm mt-1 outline-none focus:border-[#561D1A]" data-testid="new-session-name" />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={createSession} disabled={creating}
                className="px-5 py-2.5 bg-[#EC1A23] text-white rounded-xl text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50" data-testid="confirm-create-session">
                {creating ? 'Starting...' : 'Start Wizard'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <Store size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-800 mb-2">No Onboarding Sessions</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">Start a new onboarding wizard to guide a merchant through store setup.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center justify-between"
              onClick={() => openSession(s)} data-testid={`session-${s.id?.slice(0,8)}`}>
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.status === 'completed' ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                  {s.status === 'completed' ? <CheckCircle2 size={20} className="text-emerald-600" /> : <Clock size={20} className="text-blue-600" />}
                </div>
                <div>
                  <p className="font-medium text-gray-800">{s.store_name}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                    <span>Step {s.current_step || 1} of 4</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[s.status]}`}>
                      {s.status === 'in_progress' ? 'In Progress' : s.status}
                    </span>
                    <span>{formatShortDate(s.created_at)}</span>
                  </div>
                </div>
              </div>
              <ChevronRight size={18} className="text-gray-300" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
